import { Link } from "@tanstack/react-router";
import { Apple, BellRing, CalendarClock, Dumbbell, Globe, Ticket } from "lucide-react";

import { Accent, GoLink, ghostButton, glass } from "@/features/platform/components/landing-ui";
import { PlatformHead } from "@/features/platform/components/platform-chrome";
import { cn } from "@/lib/utils";
import { m } from "@/paraglide/messages.js";

/** What the app covers, as the badges resting in the light at the bottom of the hero. */
const tools = () => [
  { id: "schedule", icon: CalendarClock, label: m.platform_preview_kicker() },
  { id: "plans", icon: Dumbbell, label: m.platform_feature_plans_title() },
  { id: "diet", icon: Apple, label: m.platform_feature_diet_title() },
  { id: "packs", icon: Ticket, label: m.platform_feature_packs_title() },
  { id: "page", icon: Globe, label: m.platform_feature_page_title() },
  { id: "reminders", icon: BellRing, label: m.platform_digest_eyebrow() },
];

/**
 * The platform's opening, for the trainer deciding whether to run their business here: the claim
 * in the middle and a horizon of light rising from the bottom edge of the card. The athlete app
 * opens on a photo; this one opens on the promise and lets the sections below show the work.
 */
export const PlatformHero = () => (
  <PlatformHead className="flex min-h-[640px] flex-col md:min-h-[min(760px,calc(100svh-24px))]">
    <div className="relative z-10 mx-auto flex max-w-[860px] flex-col items-center pt-14 text-center md:pt-[clamp(40px,7vh,80px)]">
      <span
        className={cn(
          "inline-flex h-9 items-center gap-2 rounded-full pr-1.5 pl-1.5 text-[13px] font-medium sm:pr-4",
          glass,
        )}
      >
        <span className="rounded-full bg-primary px-2.5 py-1 text-[11px] font-semibold whitespace-nowrap text-primary-foreground">
          {m.platform_hero_badge()}
        </span>
        <span className="max-sm:hidden">{m.platform_hero_tag()}</span>
      </span>
      <h1 className="mt-7 text-[clamp(2.875rem,6.9vw,6.5rem)] leading-[0.96] font-medium tracking-[-0.05em] text-balance">
        {m.platform_hero_title_lead()}
        <br />
        <Accent>{m.platform_hero_title_tail()}</Accent>
      </h1>
      <p className="mt-7 max-w-[540px] text-lg leading-[1.5] tracking-[-0.01em] text-muted-foreground">
        {m.platform_hero_body()}
      </p>
      <div className="mt-8 flex flex-wrap items-center justify-center gap-2.5">
        <GoLink to="/signup" tone="accent">
          {m.platform_hero_cta()}
        </GoLink>
        <Link to="/pricing" className={ghostButton}>
          {m.platform_hero_pricing()}
        </Link>
      </div>
    </div>

    {/* A ring of light far wider than the card, so only its top arc shows above the bottom edge,
        with what the app covers resting inside it. */}
    <div className="relative mt-auto pt-20 md:pt-28">
      <span
        aria-hidden
        className="pointer-events-none absolute top-6 left-1/2 aspect-square w-[150%] -translate-x-1/2 rounded-full border-t-2 border-[color-mix(in_oklch,var(--primary)_85%,white)] shadow-[0_-30px_90px_-10px_color-mix(in_oklch,var(--primary)_50%,transparent),inset_0_40px_80px_-20px_color-mix(in_oklch,var(--primary)_28%,transparent)] md:w-[130%]"
      />
      <ul className="relative mx-auto flex max-w-[1120px] flex-wrap justify-center gap-2">
        {tools().map((tool) => (
          <li
            key={tool.id}
            className={cn(
              "inline-flex h-10 items-center gap-2 rounded-full pr-4 pl-1.5 text-[13px] font-medium",
              glass,
            )}
          >
            <span className="grid size-7 place-items-center rounded-full bg-primary/15 text-[var(--site-ink)]">
              <tool.icon aria-hidden className="size-3.5" />
            </span>
            {tool.label}
          </li>
        ))}
      </ul>
    </div>
  </PlatformHead>
);
